import { useState, useEffect, useCallback, useMemo, Fragment } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
// import Input from "../../components/Input/Input";
import Icon from "../../components/Icon/Icon";
import { login, setSessionUser, setUser, signUpUser } from "../../Reducers/actions/authActions";
import { supabase } from "../../../supabase";
import { fetchVerifyInvite } from "../../Reducers/actions/userActions";
import { useDispatch } from "react-redux";
import {
    getPasswordConfirmationValidationMessage,
    getPasswordValidationMessage,
    getTermsValidationMessage,
    getUsernameValidationMessage,
    PASSWORD_REQUIREMENTS_MESSAGE,
} from "./signUpValidation";
import './AuthCommLink.scss'
import { useKeyboardDetectionNoRoot } from "../../../helpers/hooks";
import { useSignupValidation } from "./useSignupValidation";
import { startGoogleAuth } from "./googleAuth";
import GoogleButtonContent from "./GoogleButtonContent";
import {
    SIGNUP_CREATOR_SESSION_KEY,
    VERIFY_EMAIL_BANNER_EMAIL_KEY,
    resolveUsernameFromInvite,
    resolveCreatorFromInvite,
    clearPendingSoftwallInvite,
} from "../../constants/signupFlow";

export default function SignUp() {
    const [password, setPassword] = useState("");
    const [pwdConfirm, setPwdConfirm] = useState("")
    // const [name, setName] = useState("");
    // const [surname, setSurname] = useState("");
    const [username, setUsername] = useState("");
    const [loading, setLoading] = useState(false);
    const [loadingGoogle, setLoadingGoogle] = useState(false);
    // const [isEmailSent, setIsEmailSent] = useState(false);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [error, setError] = useState(null)
    const [showPwd, setShowPwd] = useState(false)
    // const [createUserOnly, setCreateUserOnly] = useState(false)
    const [invitedBy, setInvitedBy] = useState("")
    const [step, setStep] = useState(0)
    const [savedEmail, setSavedEmail] = useState("")
    const [inviteLoading, setInviteLoading] = useState(true)
    const [usernameFromInvite, setUsernameFromInvite] = useState(false)

    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const dispatch = useDispatch()

    const { keyboardVisible } = useKeyboardDetectionNoRoot();

    // useEffect(() => {
    //     supabase.auth.signOut()
    // }, [])

    useEffect(() => {
        supabase.auth.signOut()

        // const _email = searchParams.get("email");
        const token = searchParams.get("token");
        // The "code" param here is Supabase's PKCE exchange code, not a sender ID.
        // Do not pass it to fetchVerifyInvite — there is no sender for /register invites.
        if (token) {
            fetchVerifyInvite(token, null).then(({ success, message, data }) => {
                setInviteLoading(false);
                if (!success) {
                    setError(message)
                    setTimeout(() => {
                        // navigate('/login')
                    }, 3000)
                    return;
                }
                if (data?.sender?.firstName && data?.sender?.lastName) {
                    setInvitedBy(`${data.sender.firstName} ${data.sender.lastName}`);
                } else {
                    setInvitedBy("the HYK Platform");
                }
                if (data?.email) {
                    localStorage.setItem("email", data.email)
                    setSavedEmail(data.email)
                }
                const un = resolveUsernameFromInvite(data, searchParams);
                if (un) {
                    setUsername(un.toUpperCase());
                    setUsernameFromInvite(true);
                    setStep(1);
                }
                const creator = resolveCreatorFromInvite(data, searchParams);
                sessionStorage.setItem(SIGNUP_CREATOR_SESSION_KEY, creator ? "1" : "0");
            })
        }
        else {
            setInviteLoading(false);
            navigate("/register");
        }
    }, [navigate, searchParams])

    const stepFieldsMap = useMemo(() => {
        if (usernameFromInvite) {
            return { 1: ["password", "pwdConfirm", "termsAccepted"] };
        }
        return {
            0: ["username"],
            1: ["password", "pwdConfirm", "termsAccepted"],
        };
    }, [usernameFromInvite]);

    const getFieldMessage = useCallback((fieldName) => {
        if (fieldName === "username") return getUsernameValidationMessage(username);
        if (fieldName === "password") return getPasswordValidationMessage(password);
        if (fieldName === "pwdConfirm") return getPasswordConfirmationValidationMessage(password, pwdConfirm);
        if (fieldName === "termsAccepted") return getTermsValidationMessage(termsAccepted);
        return null;
    }, [password, pwdConfirm, termsAccepted, username]);

    const {
        fieldErrors,
        touchedFields,
        setFieldErrors,
        setAttemptedSubmit,
        getStepFields,
        validateFields,
        handleFieldBlur,
        showFieldError,
        markFieldsTouched,
    } = useSignupValidation({
        step,
        stepFieldsMap,
        getFieldMessage,
    });

    const handleSignUp = async () => {
        const isValid = validateFields(getStepFields(1));
        if (!isValid) return;

        setLoading(true)
        const email = savedEmail || localStorage.getItem("email")
        signUpUser({ password, passwordVerification: pwdConfirm, email, username, token: searchParams.get("token") }, true).then(({ success, message, data: signupData }) => {
            if (success) {
                // Dispatch the user record immediately from the signup response so
                // Explore's verify-email banner can evaluate once we navigate there.
                const userRecord = Array.isArray(signupData) ? signupData[0] : signupData;
                if (userRecord?.user_id) {
                    dispatch(setUser(userRecord));
                }

                login({ email, password }).then(async ({ success, data, message }) => {
                    if (success && data && data.session) {
                        dispatch(setSessionUser(data.session))
                        await supabase.auth.setSession({
                            access_token: data.session.access_token,
                            refresh_token: data.session.refresh_token,
                        });
                        // Store email so the verify-email banner modal can pre-fill it.
                        try { localStorage.setItem(VERIFY_EMAIL_BANNER_EMAIL_KEY, email); } catch { /* ignore */ }
                        const isCreator = sessionStorage.getItem(SIGNUP_CREATOR_SESSION_KEY) === "1";
                        clearPendingSoftwallInvite();
                        if (isCreator) {
                            navigate("/add-social-links");
                        } else {
                            sessionStorage.removeItem(SIGNUP_CREATOR_SESSION_KEY);
                            navigate("/explore");
                        }
                    } else {
                        setError(message)
                        navigate('/login')
                    }
                })
            } else {
                // 'For security purposes, you can only request this after 35 seconds.'
                //  Endpoint is returning DB error
                // setError(message)
                setError(message || "Email already in use try again in 35 seconds")
            }
            setLoading(false)

        })
    };

    // const handleAnotherEmail = () => {
    //     localStorage.removeItem("email")
    //     navigate("/login")
    // }

    // const handleSendAgain = () => {
    //     const email = localStorage.getItem("email")
    //     if (email) {
    //         signUpUser({ name, surname, password, passwordVerification: pwdConfirm, email }).then(({ success, message }) => {
    //             if (success) {
    //                 setIsEmailSent(true)
    //             } else {
    //                 // 'For security purposes, you can only request this after 35 seconds.'
    //                 //  Endpoint is returning DB error
    //                 // setError(message)
    //                 setError(message || "Email already in use try again in 35 seconds")
    //             }
    //             setLoading(false)

    //         })
    //     } else {
    //         navigate("/send-invite")
    //     }
    // }

    useEffect(() => {
        return () => localStorage.removeItem("email")
    }, [])

    const handleChangeStep = () => {
        setAttemptedSubmit(true);
        const currentFields = getStepFields(step);
        const isValid = validateFields(currentFields);

        if (!isValid) {
            markFieldsTouched(currentFields);
            return;
        }

        if (step === 0) {
            setAttemptedSubmit(false);
            setStep(1);
            return;
        }

        if (step === 1) {
            handleSignUp();
        }
    }

    const handleGoogleSignup = async () => {
        setError(null);
        setLoadingGoogle(true);
        const returnTo = `${location.pathname}${location.search || ""}`;
        const result = await startGoogleAuth(returnTo);
        if (!result.success) {
            setError(result.message);
            setLoadingGoogle(false);
            return;
        }
        window.location.href = result.url;
    };

    const isGoogleAuthEnabled = import.meta.env.VITE_ENABLE_GOOGLE_AUTH !== "false";

    if (inviteLoading) {
        return (
            <div className="auth-bg pt-12 p-fixed d-flex aic jcc" style={{ height: `100%`, width: "100dvw", top: 0, left: 0 }}>
                <p className="fg-crea fw-medium">Loading…</p>
            </div>
        );
    }

    return (
        <div className="auth-bg pt-12 p-fixed" style={{ height: `100%`, width: '100dvw', top: 0, left: 0, overflow: 'scroll' }}>
            {!keyboardVisible && <div className="d-flex aic jcc my-40 pb-24">
                <Icon alias={"hyk"} width={48} color="#FFF" />
            </div>}
            <div className="auth-container pb-40 h-100">
                {step === 0 && <Fragment>
                    <h2 className="auth-title fg-crea fw-bold mb-8">WELCOME TO HYK</h2>
                    {invitedBy && invitedBy.length > 0 && <p className="fg-crea mt-0 mb-40 ta-center">You have been invited by {invitedBy ? invitedBy : "the HYK Platform"}</p>}
                </Fragment>}

                {step > 0 && !usernameFromInvite && (
                    <span className="p-absolute back-icon" onClick={() => setStep((prev) => prev - 1)}>
                        <Icon alias={"chevron-left"} width={24} color="#000" className="ml-16" />
                    </span>
                )}

                {step === 1 && <p className="auth-title fs-sm2 fg-crea fw-bold mb-8">Create a password</p>}

                {error && <p className="error-message mt-0">{error}</p>}
                <div className="auth-form mb-40">
                    {/* <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="First Name"
                        className="mb-16 input-main"
                    />
                    <input
                        type="text"
                        value={surname}
                        onChange={(e) => setSurname(e.target.value)}
                        placeholder="Last Name"
                        className="mb-16 input-main"
                    /> */}
                    {step === 0 &&
                        <Fragment>
                            <input
                                type="text"
                                value={username}
                                onChange={(e) => {
                                    const nextUsername = e.target.value.toUpperCase();
                                    setUsername(nextUsername);
                                    if (touchedFields.username) {
                                        setFieldErrors((prev) => ({ ...prev, username: getUsernameValidationMessage(nextUsername) }));
                                    }
                                }}
                                onBlur={() => handleFieldBlur("username")}
                                placeholder="USERNAME"
                                className={`mb-8 input-main ${showFieldError("username") ? 'input-error' : ''}`}
                                autoComplete="off"
                                autoCorrect="off"
                                autoCapitalize="characters"
                                spellCheck="false"
                                data-form-type="other"
                                data-lpignore="true"
                                data-1p-ignore="true"
                            />
                            {showFieldError("username") && <p className="inline-field-error mt-0 mb-12">{fieldErrors.username}</p>}
                            <p className="fg-black mb-40 fs-real fw-medium mt-0">Usernames are at least 3 characters long, all caps, and can contain letters, numbers, and underscores only.</p>
                        </Fragment>
                    }
                    {step === 1 && <Fragment>
                        <div className="p-relative">
                            <input
                                type={showPwd ? "text" : "password"}
                                value={password}
                                onChange={(e) => {
                                    const nextPassword = e.target.value;
                                    setPassword(nextPassword);
                                    if (touchedFields.password || touchedFields.pwdConfirm) {
                                        setFieldErrors((prev) => ({
                                            ...prev,
                                            password: getPasswordValidationMessage(nextPassword),
                                            pwdConfirm: getPasswordConfirmationValidationMessage(nextPassword, pwdConfirm),
                                        }));
                                    }
                                }}
                                onBlur={() => handleFieldBlur("password")}
                                placeholder="Password"
                                className={`mb-8 input-main w-full ${showFieldError("password") ? "input-error" : ""}`}
                                autoComplete="new-password"
                                autoCorrect="off"
                                spellCheck="false"
                                data-form-type="other"
                                data-lpignore="true"
                                data-1p-ignore="true"
                            />
                            <Icon alias={"show-password"} width={24} color="#777777" className={'show-icon'} onClick={() => setShowPwd(prev => !prev)} />
                        </div>
                        {showFieldError("password") && <p className="inline-field-error mt-0 mb-12">{fieldErrors.password}</p>}
                        <p className="fg-black fs-real fw-medium mb-16 mt-0">{PASSWORD_REQUIREMENTS_MESSAGE}</p>
                        <div className="p-relative">
                            <input
                                type={showPwd ? "text" : "password"}
                                value={pwdConfirm}
                                onChange={(e) => {
                                    const nextPasswordConfirm = e.target.value;
                                    setPwdConfirm(nextPasswordConfirm);
                                    if (touchedFields.pwdConfirm) {
                                        setFieldErrors((prev) => ({
                                            ...prev,
                                            pwdConfirm: getPasswordConfirmationValidationMessage(password, nextPasswordConfirm),
                                        }));
                                    }
                                }}
                                onBlur={() => handleFieldBlur("pwdConfirm")}
                                placeholder="Password confirmation"
                                className={`mb-8 input-main w-full ${showFieldError("pwdConfirm") ? "input-error" : ""}`}
                                autoComplete="new-password"
                                autoCorrect="off"
                                spellCheck="false"
                                data-form-type="other"
                                data-lpignore="true"
                                data-1p-ignore="true"
                            />
                            <Icon alias={"show-password"} width={24} color="#777777" className={'show-icon'} onClick={() => setShowPwd(prev => !prev)} />
                        </div>
                        {showFieldError("pwdConfirm") && <p className="inline-field-error mt-0 mb-12">{fieldErrors.pwdConfirm}</p>}
                        <div className="d-flex aic mb-24">
                            <input
                                type="checkbox"
                                id="terms"
                                name="terms"
                                checked={termsAccepted}
                                onChange={() => {
                                    const nextValue = !termsAccepted;
                                    setTermsAccepted(nextValue);
                                    if (touchedFields.termsAccepted) {
                                        setFieldErrors((prev) => ({ ...prev, termsAccepted: getTermsValidationMessage(nextValue) }));
                                    }
                                }}
                                onBlur={() => handleFieldBlur("termsAccepted")}
                            />
                            <label htmlFor="terms" className="ml-8 fs-xs">I agree to the <a href="/terms" target="_blank" className="link-btn">Terms & Conditions</a> and <a href="/privacy" target="_blank" className="link-btn">Privacy Policy</a>.</label>
                        </div>
                        {showFieldError("termsAccepted") && <p className="inline-field-error mt-0 mb-12">{fieldErrors.termsAccepted}</p>}
                    </Fragment>}


                    <div className='p-fixed bg-white auth-footer mt-24 pb-24' style={{ bottom: '0', left: '0' }}>
                        <button className="btn red-btn btn-full-v d-flex aic jcc p-relative" onClick={handleChangeStep} disabled={loading || loadingGoogle}>
                            {loading ? "Signing Up..." : "Continue"}
                        </button>
                        {isGoogleAuthEnabled && step === 0 && !usernameFromInvite && (
                            <button
                                type="button"
                                className="btn btn-full-v mt-16 google-auth-btn"
                                onClick={handleGoogleSignup}
                                disabled={loading || loadingGoogle}
                                aria-label="Continue with Google"
                            >
                                <GoogleButtonContent loading={loadingGoogle} />
                            </button>
                        )}
                        {/* <div className="mb-24">
                            <p className="fg-crea fs-xxs fw-medium">ALREADY HAVE AN ACCOUNT? <a href="/login" className="fg-red-pink fw-medium td-underline">LOGIN</a></p>
                        </div> */}
                    </div>
                </div>
            </div>
        </div>
    );
}
